import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Avatar from '@mui/material/Avatar';
import { colorCodesForAvatar } from '../../../../constants/index';
import { chracterPosition, getStatusStyle } from '../../../../utils/index';
import { ReactComponent as Urgent } from '../../../../assets/images/TicketImages/urgent.svg';
import { ReactComponent as High } from '../../../../assets/images/TicketImages/high.svg';
import { ReactComponent as Medium } from '../../../../assets/images/TicketImages/medium.svg';
import { ReactComponent as Low } from '../../../../assets/images/TicketImages/low.svg';
import HighlightText from '../HighlightText/HighlightText';
import { TICKETS_HOME_PATH } from '../../../../config/featureFlags';

const headerSx = {
    fontWeight: 400,
    fontSize: '12px',
    color: '#405261',
    fontFamily: 'Inter',
    paddingBottom: '0px',
    paddingLeft: '0px',
    borderBottom: '1px solid #E5E9EC',
};

const cellSx = {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    color: '#405261',
    borderBottom: '1px solid #E5E9EC',
    py: 0,
    paddingLeft: '0px',
    verticalAlign: 'middle',
};

const PriorityIcon = ({ priority }) => {
    switch (priority) {
        case 'urgent': return <Urgent />;
        case 'high': return <High />;
        case 'medium': return <Medium />;
        case 'low': return (
            <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Low />
            </Box>
        );
        default: return null;
    }
};

const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear() % 100).padStart(2, '0')}`;
};

import { useNavigate } from 'react-router-dom';

const TicketsTable = ({ tickets, searchQuery = '' }) => {
    const navigate = useNavigate();
    if (!tickets || tickets.length === 0) return null;

    return (
        <Box sx={{ marginBottom: '32px' }}>
            <Typography sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '18px', fontWeight: 500, lineHeight: '18px', marginBottom: '5px' }}>
                Tickets ({tickets.length})
            </Typography>

            <TableContainer sx={{ boxShadow: 'none' }}>
                <Table sx={{ minWidth: 750 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headerSx, paddingLeft: '0px', width: '8%' }}>ID</TableCell>
                            <TableCell sx={{ ...headerSx, width: '32%' }}>Subject</TableCell>
                            <TableCell sx={{ ...headerSx, width: '10%' }}>Status</TableCell>
                            <TableCell sx={{ ...headerSx, width: '15%' }}>Assignee</TableCell>
                            <TableCell sx={{ ...headerSx, width: '25%' }}>Customer Name</TableCell>
                            <TableCell sx={{ ...headerSx, width: '10%' }}>Waiting</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.map((ticket) => {
                            const ticketId = ticket.ticketNumber || ticket._id?.slice(-5) || 'N/A';
                            const assigneeDisplay = ticket.assigneeName || ticket.assignee?.full_name || 'Unassigned';
                            const customerName = ticket.customerName || 'N/A';
                            const customerEmail = ticket.customerEmail || '';

                            return (
                                <TableRow
                                    key={ticket._id}
                                    hover
                                    onClick={() => navigate(`${TICKETS_HOME_PATH}?sortOrder=desc&sortField=updatedAt&page=0&ticketId=${ticket._id}`)}
                                    sx={{
                                        cursor: 'pointer',
                                        '& td': { height: '4rem', py: 0, verticalAlign: 'middle' },
                                    }}
                                >
                                    <TableCell sx={{ ...cellSx, padding: '0px', color: '#061522' }}>
                                        #{ticketId}
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Box sx={{ display: 'flex' }}>
                                                <PriorityIcon priority={ticket.priority} />
                                            </Box>
                                            <HighlightText
                                                text={(() => {
                                                    const subject = ticket.subject || 'N/A';
                                                    const capitalized = subject.charAt(0).toUpperCase() + subject.slice(1);
                                                    return capitalized.length > 32 ? capitalized.slice(0, 32) + '...' : capitalized;
                                                })()}
                                                query={searchQuery}
                                                sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '14px' }}
                                            />
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <span style={getStatusStyle(ticket.status)}>
                                            {ticket.status?.charAt(0).toUpperCase() + ticket.status?.slice(1)}
                                        </span>
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <HighlightText text={assigneeDisplay} query={searchQuery} sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '14px' }} />
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: colorCodesForAvatar[chracterPosition(customerName.substring(0, 1).toUpperCase())],
                                                    width: '32px',
                                                    height: '32px',
                                                }}
                                            >
                                                {customerName.substring(0, 1).toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <p className="team-table-rowtext truncate" style={{ margin: 0 }}>
                                                    <HighlightText
                                                        text={customerName.charAt(0).toUpperCase() + customerName.slice(1)}
                                                        query={searchQuery}
                                                        sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#061522', fontWeight: 500 }}
                                                    />
                                                </p>
                                                {customerEmail && (
                                                    <p className="truncate" style={{ fontSize: '14px', color: '#556575', margin: 0 }}>
                                                        <HighlightText
                                                            text={customerEmail.toLowerCase()}
                                                            query={searchQuery}
                                                            sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#556575' }}
                                                        />
                                                    </p>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        {formatTimestamp(ticket.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TicketsTable;
